/**
 * Organization-status transition DAG — pure-TypeScript single source of truth.
 *
 * This module owns the canonical shape of the organization-lifecycle state
 * machine. Both the domain entity (`apps/api/src/modules/identity/domain/
 * organization/organization.entity.ts`) and the Zod validator
 * (`@artemis/validators/organization-status`) consume these primitives so
 * there is exactly one definition of the tuple, the status type, the
 * allowed-transition graph, and the transition guard.
 *
 * Pure TypeScript by construction — no `zod`, no `@nestjs/*`, no framework
 * imports (AUDIT-216 domain-purity invariant). The `OrganizationStatus`
 * type is derived directly from the tuple via `(typeof T)[number]`, never
 * via `z.infer`.
 *
 * SCRUM-311 (Desata ISA lifecycle) — the ISA (Individual System Account)
 * FSM has five statuses and eight edges; `closed` is terminal:
 *
 *   onboarding → {active, closed}
 *   training   → {active, closed}
 *   testing    → {onboarding, active, closed}
 *   active     → {closed}
 *   closed     → {} (terminal — no reactivation)
 *
 * `onboarding`, `training`, and `testing` are pre-production entry states; an
 * ISA is created in one of them (`ORGANIZATION_START_STATUSES`). `training`
 * has no inbound edge, so it is reachable ONLY at creation time. Nothing
 * transitions INTO `testing` or `training`; `testing` may step into
 * `onboarding` (technical validation complete → client onboarding).
 */
export const ORGANIZATION_STATUSES = [
    'onboarding',
    'training',
    'testing',
    'active',
    'closed',
];
export const ALLOWED_TRANSITIONS = {
    onboarding: new Set(['active', 'closed']),
    training: new Set(['active', 'closed']),
    testing: new Set(['onboarding', 'active', 'closed']),
    active: new Set(['closed']),
    closed: new Set(),
};
export function canTransitionOrganizationStatus(from, to) {
    // Defensive: `from` may arrive as an unknown/coerced string (JSON
    // deserialization, stale DB row, injection probe). `ALLOWED_TRANSITIONS`
    // is a plain object, so a prototype-shaped key (`__proto__`, `constructor`,
    // `toString`) would resolve to an inherited member and make
    // `ALLOWED_TRANSITIONS[from]?.has(to)` THROW (a function has no `.has`).
    // The own-property guard fails such keys closed to `false` instead.
    if (!Object.prototype.hasOwnProperty.call(ALLOWED_TRANSITIONS, from)) {
        return false;
    }
    return ALLOWED_TRANSITIONS[from].has(to);
}
/**
 * The statuses an ISA may be created in (the pre-production entry states).
 * `active` and `closed` are reachable only via a transition, never directly
 * at creation. `training` lives here because it has no inbound transition
 * edge — creation is its only entry point.
 */
export const ORGANIZATION_START_STATUSES = [
    'onboarding',
    'training',
    'testing',
];
export function isOrganizationStartStatus(value) {
    return (typeof value === 'string' &&
        ORGANIZATION_START_STATUSES.includes(value));
}
//# sourceMappingURL=organization-status.js.map